import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';
import { useTripReservations } from '../../hooks/useTripReservations';
import {
  decodeReservation, encodeReservation, sameReservation,
  type ReservationGroups, type ReservationOption,
} from '../../utils/reservations';
import type { ReservationLink, ReservationType } from '../../types';

const GROUP_ORDER: ReservationType[] = ['FLIGHT', 'HOTEL', 'BOOKING'];

interface Props {
  tripId: string;
  value: ReservationLink | null;
  onChange: (option: ReservationOption | null) => void;
  /** Already-loaded reservations; without them the list is fetched. */
  reservations?: ReservationGroups;
}

/** Optional "which ticket did this pay for" picker. Every reservation, grouped. */
export function ReservationSelect({ tripId, value, onChange, reservations }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const groups = useTripReservations(tripId, reservations);

  if (!groups) return null;
  const all = GROUP_ORDER.flatMap(g => groups[g]);
  if (all.length === 0) return null;

  return (
    <div>
      <label className="label" htmlFor="expense-reservation">{t('expenses.form.reservation')}</label>
      <select
        id="expense-reservation"
        className="input"
        value={encodeReservation(value)}
        onChange={e => {
          const link = decodeReservation(e.target.value);
          onChange(all.find(o => sameReservation(o.link, link)) ?? null);
        }}
      >
        <option value="">{t('expenses.form.reservationNone')}</option>
        {GROUP_ORDER.filter(g => groups[g].length > 0).map(g => (
          <optgroup key={g} label={t(`expenses.reservationGroups.${g}`)}>
            {groups[g].map(o => (
              <option key={o.link.id} value={encodeReservation(o.link)}>
                {o.title} · {format(parseISO(o.date), 'd MMM', { locale: dateLocale })}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}
